import React, { useState, useEffect, useCallback } from 'react';
import {
    Page,
    Layout,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Button,
    Spinner,
    Banner,
    DataTable,
    Select,
    Tabs,
    Divider
} from '@shopify/polaris';
import { financialReportsService } from '../services/financialReportsService';
import { accountingHelpers } from '../constants/accountingConstants';
import KPICard from '../components/accounting/KPICard';

const FinancialReportsPage = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);

    // Period selector
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [reportType, setReportType] = useState('pnl');

    // Load data based on report type
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let data;
            switch (reportType) {
                case 'pnl':
                    data = await financialReportsService.getMonthlyPnL(year, month);
                    break;
                case 'cash_flow':
                    data = await financialReportsService.getMonthlyCashFlow(year, month);
                    break;
                case 'trends':
                    data = await financialReportsService.getRevenueTrend(12);
                    break;
                case 'executive':
                    data = await financialReportsService.getExecutiveSummary(year, month);
                    break;
                default:
                    data = await financialReportsService.getMonthlyPnL(year, month);
            }
            setReportData(data.data || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [year, month, reportType]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Export handlers
    const handleExportCSV = () => {
        // Convert report data to CSV
        if (!reportData) return;

        let csvContent = 'data:text/csv;charset=utf-8,';

        if (reportType === 'pnl') {
            csvContent += 'Category,Amount\n';
            csvContent += `Revenue,${reportData.revenue?.total || 0}\n`;
            csvContent += `COGS,${reportData.cogs?.total || 0}\n`;
            csvContent += `Gross Profit,${reportData.gross_profit || 0}\n`;
            csvContent += `Operating Expenses,${reportData.expenses?.total || 0}\n`;
            csvContent += `Net Profit,${reportData.net_profit || 0}\n`;
        }

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `${reportType}_${year}_${month}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Render P&L Report
    const renderPnLReport = () => {
        if (!reportData) return null;

        const { revenue, cogs, gross_profit, expenses, net_profit, gross_margin, net_margin } = reportData;

        return (
            <BlockStack gap="500">
                {/* Summary Cards */}
                <InlineStack gap="400">
                    <KPICard title="Total Revenue" value={revenue?.total || 0} format="currency" />
                    <KPICard title="Gross Profit" value={gross_profit || 0} format="currency" subtitle={`${(gross_margin || 0).toFixed(1)}% margin`} />
                    <KPICard title="Net Profit" value={net_profit || 0} format="currency" subtitle={`${(net_margin || 0).toFixed(1)}% margin`} trend={net_profit >= 0 ? 'up' : 'down'} />
                </InlineStack>

                {/* Revenue Section */}
                <Card>
                    <BlockStack gap="300">
                        <Text as="h3" variant="headingMd">Revenue</Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric']}
                            headings={['Source', 'Amount']}
                            rows={[
                                ['Order Revenue', accountingHelpers.formatCurrency(revenue?.orders || 0)],
                                ['Other Revenue', accountingHelpers.formatCurrency(revenue?.other || 0)],
                                ['Total Revenue', accountingHelpers.formatCurrency(revenue?.total || 0)]
                            ]}
                        />
                    </BlockStack>
                </Card>

                {/* COGS Section */}
                <Card>
                    <BlockStack gap="300">
                        <Text as="h3" variant="headingMd">Cost of Goods Sold</Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric']}
                            headings={['Category', 'Amount']}
                            rows={[
                                ['Production Costs', accountingHelpers.formatCurrency(cogs?.production || 0)],
                                ['Materials', accountingHelpers.formatCurrency(cogs?.materials || 0)],
                                ['Shipping', accountingHelpers.formatCurrency(cogs?.shipping || 0)],
                                ['Total COGS', accountingHelpers.formatCurrency(cogs?.total || 0)]
                            ]}
                        />
                    </BlockStack>
                </Card>

                <Divider />
                <InlineStack align="space-between">
                    <Text as="p" variant="headingMd">Gross Profit</Text>
                    <Text as="p" variant="headingMd" tone={gross_profit >= 0 ? 'success' : 'critical'}>
                        {accountingHelpers.formatCurrency(gross_profit || 0)}
                    </Text>
                </InlineStack>

                {/* Expenses Section */}
                <Card>
                    <BlockStack gap="300">
                        <Text as="h3" variant="headingMd">Operating Expenses</Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric']}
                            headings={['Category', 'Amount']}
                            rows={Object.entries(expenses?.by_category || {}).map(([cat, amount]) => [
                                accountingHelpers.getExpenseCategoryLabel(cat),
                                accountingHelpers.formatCurrency(amount)
                            ]).concat([['Total Expenses', accountingHelpers.formatCurrency(expenses?.total || 0)]])}
                        />
                    </BlockStack>
                </Card>

                <Divider />
                <InlineStack align="space-between">
                    <Text as="p" variant="headingLg" fontWeight="bold">Net Profit</Text>
                    <Text as="p" variant="headingLg" fontWeight="bold" tone={net_profit >= 0 ? 'success' : 'critical'}>
                        {accountingHelpers.formatCurrency(net_profit || 0)}
                    </Text>
                </InlineStack>
            </BlockStack>
        );
    };

    // Render Cash Flow Report
    const renderCashFlowReport = () => {
        if (!reportData) return null;

        const { inflows, outflows, net_cash_flow, opening_balance, closing_balance } = reportData;

        return (
            <BlockStack gap="500">
                <InlineStack gap="400">
                    <KPICard title="Cash Inflows" value={inflows?.total || 0} format="currency" trend="up" />
                    <KPICard title="Cash Outflows" value={outflows?.total || 0} format="currency" trend="down" />
                    <KPICard title="Net Cash Flow" value={net_cash_flow || 0} format="currency" trend={net_cash_flow >= 0 ? 'up' : 'down'} />
                </InlineStack>

                <Card>
                    <BlockStack gap="300">
                        <Text as="h3" variant="headingMd">Cash Inflows</Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric']}
                            headings={['Source', 'Amount']}
                            rows={Object.entries(inflows?.by_source || {}).map(([src, amount]) => [
                                src, accountingHelpers.formatCurrency(amount)
                            ]).concat([['Total Inflows', accountingHelpers.formatCurrency(inflows?.total || 0)]])}
                        />
                    </BlockStack>
                </Card>

                <Card>
                    <BlockStack gap="300">
                        <Text as="h3" variant="headingMd">Cash Outflows</Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric']}
                            headings={['Category', 'Amount']}
                            rows={Object.entries(outflows?.by_category || {}).map(([cat, amount]) => [
                                accountingHelpers.getExpenseCategoryLabel(cat),
                                accountingHelpers.formatCurrency(amount)
                            ]).concat([['Total Outflows', accountingHelpers.formatCurrency(outflows?.total || 0)]])}
                        />
                    </BlockStack>
                </Card>

                <Card>
                    <BlockStack gap="200">
                        <InlineStack align="space-between">
                            <Text as="p">Opening Balance</Text>
                            <Text as="p">{accountingHelpers.formatCurrency(opening_balance || 0)}</Text>
                        </InlineStack>
                        <InlineStack align="space-between">
                            <Text as="p">Net Cash Flow</Text>
                            <Text as="p" tone={net_cash_flow >= 0 ? 'success' : 'critical'}>
                                {net_cash_flow >= 0 ? '+' : ''}{accountingHelpers.formatCurrency(net_cash_flow || 0)}
                            </Text>
                        </InlineStack>
                        <Divider />
                        <InlineStack align="space-between">
                            <Text as="p" fontWeight="bold">Closing Balance</Text>
                            <Text as="p" fontWeight="bold">{accountingHelpers.formatCurrency(closing_balance || 0)}</Text>
                        </InlineStack>
                    </BlockStack>
                </Card>
            </BlockStack>
        );
    };

    // Render Trends Report
    const renderTrendsReport = () => {
        if (!reportData?.months) return null;

        const months = reportData.months || [];

        return (
            <BlockStack gap="500">
                <Card>
                    <BlockStack gap="300">
                        <Text as="h3" variant="headingMd">Revenue Trend (Last 12 Months)</Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                            headings={['Month', 'Revenue', 'Orders', 'Avg Order Value']}
                            rows={months.map(m => [
                                m.month,
                                accountingHelpers.formatCurrency(m.revenue || 0),
                                m.orders || 0,
                                accountingHelpers.formatCurrency(m.avg_order_value || 0)
                            ])}
                        />
                    </BlockStack>
                </Card>
            </BlockStack>
        );
    };

    // Render Executive Summary
    const renderExecutiveSummary = () => {
        if (!reportData) return null;

        const { overview, kpis, highlights } = reportData;

        return (
            <BlockStack gap="500">
                <InlineStack gap="400">
                    <KPICard title="Total Revenue" value={overview?.revenue || 0} format="currency" change={kpis?.revenue_growth} trend={kpis?.revenue_growth >= 0 ? 'up' : 'down'} />
                    <KPICard title="Net Profit" value={overview?.net_profit || 0} format="currency" change={kpis?.profit_growth} trend={kpis?.profit_growth >= 0 ? 'up' : 'down'} />
                    <KPICard title="Total Orders" value={overview?.orders || 0} format="number" />
                    <KPICard title="Profit Margin" value={kpis?.profit_margin || 0} format="percentage" />
                </InlineStack>

                {highlights && highlights.length > 0 && (
                    <Card>
                        <BlockStack gap="300">
                            <Text as="h3" variant="headingMd">Key Highlights</Text>
                            {highlights.map((h, i) => (
                                <Text key={i} as="p">• {h}</Text>
                            ))}
                        </BlockStack>
                    </Card>
                )}
            </BlockStack>
        );
    };

    // Render content based on report type
    const renderReportContent = () => {
        switch (reportType) {
            case 'pnl': return renderPnLReport();
            case 'cash_flow': return renderCashFlowReport();
            case 'trends': return renderTrendsReport();
            case 'executive': return renderExecutiveSummary();
            default: return renderPnLReport();
        }
    };

    // Generate year/month options
    const yearOptions = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
        yearOptions.push({ label: String(y), value: String(y) });
    }

    const monthOptions = [
        { label: 'January', value: '1' }, { label: 'February', value: '2' },
        { label: 'March', value: '3' }, { label: 'April', value: '4' },
        { label: 'May', value: '5' }, { label: 'June', value: '6' },
        { label: 'July', value: '7' }, { label: 'August', value: '8' },
        { label: 'September', value: '9' }, { label: 'October', value: '10' },
        { label: 'November', value: '11' }, { label: 'December', value: '12' }
    ];

    const reportTypeOptions = [
        { label: 'Profit & Loss', value: 'pnl' },
        { label: 'Cash Flow', value: 'cash_flow' },
        { label: 'Revenue Trends', value: 'trends' },
        { label: 'Executive Summary', value: 'executive' }
    ];

    if (loading) {
        return (
            <Page title="Financial Reports">
                <Layout>
                    <Layout.Section>
                        <Card><InlineStack align="center"><Spinner size="large" /></InlineStack></Card>
                    </Layout.Section>
                </Layout>
            </Page>
        );
    }

    return (
        <Page
            title="Financial Reports"
            subtitle="Advanced financial analysis and reporting"
            secondaryActions={[
                { content: 'Export CSV', onAction: handleExportCSV },
                { content: 'Print', onAction: () => window.print() }
            ]}
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
                    </Layout.Section>
                )}

                {/* Controls */}
                <Layout.Section>
                    <Card>
                        <InlineStack gap="400" blockAlign="end">
                            <Select
                                label="Report Type"
                                options={reportTypeOptions}
                                value={reportType}
                                onChange={setReportType}
                            />
                            {reportType !== 'trends' && (
                                <>
                                    <Select label="Year" options={yearOptions} value={String(year)} onChange={(v) => setYear(parseInt(v))} />
                                    <Select label="Month" options={monthOptions} value={String(month)} onChange={(v) => setMonth(parseInt(v))} />
                                </>
                            )}
                            <Button onClick={loadData}>Generate</Button>
                        </InlineStack>
                    </Card>
                </Layout.Section>

                {/* Report Content */}
                <Layout.Section>
                    {renderReportContent()}
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default FinancialReportsPage;
